import Modal from '../Modal/Modal';
import styled from 'styled-components';
import {useSelector, useDispatch} from 'react-redux';
import React, {useEffect, useState} from 'react';
import {labels} from '../../../util/utils';
import {useCreateEntityForm, useAresUrlParameters} from '../../../util/Ares/customHooks';
import {useEngagementId} from '../../../util/customHooks';
import {entities, formBodyType, documentTypes, validationTypes, clientSideEventNames} from '../../../util/uiconstants';
import {getValidationsForDocumentId} from '../../../selectors/Forms/canvasFormSelectors';
import {setFormV2ToasterVisibility} from '../../../actions/toastactions';
import PropTypes from 'prop-types';

export default function EntityFormCreator({
	text,
	bodyTypeId,
	onCreationFinished,
	className = '',
	skipConfirmation = true
}) {
	const {taskId, documentId} = useAresUrlParameters();
	const validations = useSelector((store) => getValidationsForDocumentId(store, documentId));
	const [createDocumentModal, setCreateDocumentModal] = useState(false);
	const [showSuccessBanner, setShowSuccessBanner] = useState(false);
	const [objectIds, setObjectIds] = useState([]);
	const engagementId = useEngagementId();
	const entityId = entities.Account;
	const dispatch = useDispatch();

	const FormBodyTypeDocumentTypeMap = {
		[formBodyType.AnalyticsOverview]: {
			documentTypeId: documentTypes.ANALYTICSDOCUMENT,
			validationTypeId: validationTypes.AccountWithoutIndividualAnalyticForm
		},
		[formBodyType.ALRAAccountList]: {
			documentTypeId: documentTypes.ALRA_INDIVIDUAL,
			validationTypeId: validationTypes.AccountWithoutIndividualRiskAssessmentForm
		}	
	};

	const documentTypeIdToBeCreated =
		bodyTypeId !== undefined ? FormBodyTypeDocumentTypeMap[bodyTypeId].documentTypeId : null;
	const validationTypeId = bodyTypeId !== undefined ? FormBodyTypeDocumentTypeMap[bodyTypeId].validationTypeId : null;

	const CreateMissingDocument = () => {
		setShowSuccessBanner(true);
		return dispatch(useCreateEntityForm(engagementId, entityId, documentTypeIdToBeCreated, taskId, objectIds))
			.then(() => {
				window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
			})
			.catch((error) => {
				dispatch(setFormV2ToasterVisibility(true, 'error', error.message));
			})
			.finally(() => {
				onCreationFinished();
				setShowSuccessBanner(false);
				setCreateDocumentModal(false);
			});
	};

	const renderSuccessBanner = () => {
		const successMsg = labels.accountDocumentsCreated;
		dispatch(setFormV2ToasterVisibility(true, 'success', successMsg)).then(() => {
			setTimeout(() => {
				dispatch(setFormV2ToasterVisibility(false, '', ''));
				setShowSuccessBanner(false);
			}, 5000);
		});
	};

	const handleClick = () => {
		if (skipConfirmation) {
			CreateMissingDocument();
		} else {
			setCreateDocumentModal(true);
		}
	};

	useEffect(() => {
		const ids =
			validations?.length > 0
				? validations?.filter((t) => t.validationTypeId === validationTypeId)[0]?.children[0]?.children[0]?.children[0]
						?.uniqueIdList
				: [];
		if (ids?.length > 0) {
			setObjectIds(ids);
		}
	}, [validations]);

	return (
		<>
			<StyledEntityFormCreatorSection className={`StyledEntityFormCreatorSection ${className}`} onClick={handleClick}>
				{text}
			</StyledEntityFormCreatorSection>
			{showSuccessBanner && renderSuccessBanner()}
			{createDocumentModal && (
				<Modal
					show={createDocumentModal}
					showButtons
					title={labels.createMissingDocument}
					bodyText={labels.createMissingDocumentBody}
					confirmBtnTitle={labels.confirm}
					isCancelButtonDisabled={false}
					hideCancelButton={false}
					closeBtnTitle={labels.cancelLabel}
					closeTitle={labels.cancelLabel}
					onOkClick={() => CreateMissingDocument()}
					onHide={() => setCreateDocumentModal(false)}
					modalsize="small"
				>
					<PopupContent>{labels.createMissingDocumentBody}</PopupContent>
				</Modal>
			)}
		</>
	);
}

const PopupContent = styled.section`
	font-size: var(--px-14);
	font-weight: 300;
	line-height: var(--px-24);
	color: var(--neutrals-900);
	.account-name {
		font-weight: bold;
	}
`;

const StyledEntityFormCreatorSection = styled.section`
	width: 100%;
`;

EntityFormCreator.propTypes = {
	text: PropTypes.string.isRequired,
	bodyTypeId: PropTypes.string,
	onCreationFinished: PropTypes.func.isRequired,
	className: PropTypes.string,
	skipConfirmation: PropTypes.bool
};
